"use client";

import { createContext, useContext, useEffect, useMemo, useState } from "react";

const AuthContext = createContext(null);

const STORAGE_TOKEN = "auth_token";
const STORAGE_USER = "auth_user";

function safeJsonParse(raw) {
  try {
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function normalizeRoleFromUser(u) {
  const roleId = u?.role_id ?? u?.rolId ?? u?.roleId ?? null;
  const roleName = u?.role ?? u?.rol ?? u?.roleName ?? null;
  return roleId ?? roleName ?? null;
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authError, setAuthError] = useState(null);

  // Cargar sesión 1 sola vez
  useEffect(() => {
    try {
      const storedToken = localStorage.getItem(STORAGE_TOKEN);
      const storedUser = localStorage.getItem(STORAGE_USER);

      if (storedToken && storedUser) {
        const parsedUser = safeJsonParse(storedUser);
        if (parsedUser) {
          setUser(parsedUser);
          setUserRole(normalizeRoleFromUser(parsedUser));
        } else {
          localStorage.removeItem(STORAGE_USER);
          localStorage.removeItem(STORAGE_TOKEN);
          setUser(null);
          setUserRole(null);
        }
      } else {
        setUser(null);
        setUserRole(null);
      }
    } catch (e) {
      console.error("Error leyendo sesión:", e);
      setUser(null);
      setUserRole(null);
    } finally {
      setLoading(false);
    }
  }, []);

  const login = (userFromApi, token) => {
    try {
      localStorage.setItem(STORAGE_TOKEN, token || "local-login");

      if (userFromApi) {
        localStorage.setItem(STORAGE_USER, JSON.stringify(userFromApi));
        setUser(userFromApi);
        setUserRole(normalizeRoleFromUser(userFromApi));
      }

      setAuthError(null);
    } catch (e) {
      console.error("Error guardando sesión:", e);
      setAuthError("No se pudo guardar la sesión");
    }
  };

  const logout = () => {
    try {
      localStorage.removeItem(STORAGE_TOKEN);
      localStorage.removeItem(STORAGE_USER);
      sessionStorage.clear();
    } catch (e) {
      console.error("Error limpiando sesión:", e);
    } finally {
      setUser(null);
      setUserRole(null);
      setAuthError(null);
      window.location.replace("/login"); // ← redirige siempre al login
    }
  };

  const value = useMemo(
    () => ({
      user,
      userRole,
      loading,
      isLoading: loading,
      authError,
      setAuthError,
      login,
      logout,
    }),
    [user, userRole, loading, authError]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth debe ser usado dentro de un AuthProvider");
  return ctx;
}